import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { formatDate } from "../../lib/format";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Button } from "../ui/button";
import { Bell, CalendarClock, RefreshCw, CheckCircle2, Users, Check, FileText } from "lucide-react";

const ICONS = { due_soon: CalendarClock, renewal: RefreshCw, emi_completion: CheckCircle2, reimbursement: Users, digest: FileText };

export default function NotificationsBell({ compact = false }) {
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  const loadCount = useCallback(async () => {
    try { const { data } = await api.get("/notifications/unread-count"); setCount(data.count); } catch {}
  }, []);

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 60000);
    return () => clearInterval(t);
  }, [loadCount]);

  const openPanel = async (o) => {
    setOpen(o);
    if (o) {
      try {
        await Promise.all([api.post("/notifications/generate"), api.post("/notifications/digest")]);
        const { data } = await api.get("/notifications");
        setItems(data);
      } catch {}
    }
  };

  const markAll = async () => {
    // optimistic clear; avoid immediate re-fetch race that can flash the stale count
    try { await api.post("/notifications/read-all"); setItems((x) => x.map((n) => ({ ...n, read: true }))); setCount(0); } catch {}
  };

  const clickItem = async (n) => {
    if (!n.read) { try { await api.patch(`/notifications/${n.id}/read`); } catch {} setCount((c) => Math.max(0, c - 1)); }
    setItems((x) => x.map((i) => (i.id === n.id ? { ...i, read: true } : i)));
    setOpen(false);
    if (n.commitment_id) nav("/app/commitments");
  };

  return (
    <Popover open={open} onOpenChange={openPanel}>
      <PopoverTrigger asChild>
        <button className={`relative ${compact ? "p-2" : "flex items-center gap-3 px-4 py-2.5 rounded-xl text-slatey hover:bg-secondary w-full transition-colors"}`} data-testid="notifications-bell">
          <Bell size={compact ? 20 : 18} strokeWidth={1.5} className={compact ? "text-ink" : ""} />
          {!compact && <span className="text-sm font-medium">Notifications</span>}
          {count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 md:static md:ml-auto h-5 min-w-[20px] px-1 rounded-full bg-mint text-white text-[10px] flex items-center justify-center" data-testid="notifications-count">
              {count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <p className="text-sm font-medium text-ink">Notifications</p>
          {items.some((n) => !n.read) && (
            <button onClick={markAll} className="text-xs text-mint" data-testid="notifications-readall">Mark all read</button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground">You're all caught up.</div>
          ) : items.map((n) => {
            const Icon = ICONS[n.type] || Bell;
            return (
              <button key={n.id} onClick={() => clickItem(n)} data-testid={`notification-${n.id}`}
                className={`w-full text-left flex gap-3 px-4 py-3 border-b border-border/50 hover:bg-secondary/60 transition-colors ${n.read ? "opacity-60" : ""}`}>
                <div className="h-8 w-8 rounded-lg bg-secondary flex items-center justify-center shrink-0"><Icon size={15} className="text-navy" /></div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-ink">{n.title}</p>
                  <p className="text-xs text-slatey mt-0.5">{n.body}</p>
                  <p className="text-[10px] text-muted-foreground mt-1">{formatDate(n.created_at)}</p>
                </div>
                {!n.read && <span className="h-2 w-2 rounded-full bg-mint shrink-0 mt-1.5" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
